import React, { useState } from 'react';
import { motion } from 'framer-motion';

const MapSection: React.FC = () => {
  const [selectedShop, setSelectedShop] = useState<number | null>(null);
  
  // Données mockées des commerces
  const shops = [
    { 
      id: 1, 
      name: "Café des Artistes", 
      type: "Restaurant",
      position: { top: "30%", left: "40%" },
      points: 10,
      address: "12 rue des Lilas, Centre-ville",
      description: "Café-restaurant proposant des plats faits maison et une ambiance chaleureuse."
    },
    { 
      id: 2, 
      name: "Librairie Page & Plume", 
      type: "Librairie",
      position: { top: "45%", left: "60%" },
      points: 5,
      address: "8 avenue des Écrivains, Centre-ville",
      description: "Librairie indépendante spécialisée en littérature française et internationale."
    },
    { 
      id: 3, 
      name: "Boulangerie Tradition", 
      type: "Alimentation",
      position: { top: "65%", left: "35%" },
      points: 3,
      address: "3 place du Marché, Centre-ville",
      description: "Boulangerie artisanale proposant pains, viennoiseries et pâtisseries faits maison."
    },
    { 
      id: 4, 
      name: "Mode Citadine", 
      type: "Vêtements",
      position: { top: "25%", left: "70%" },
      points: 8,
      address: "15 rue du Commerce, Centre-ville",
      description: "Boutique de prêt-à-porter proposant des marques locales et éco-responsables."
    },
    { 
      id: 5, 
      name: "Pharmacie Centrale", 
      type: "Santé",
      position: { top: "55%", left: "25%" },
      points: 4,
      address: "22 boulevard Principal, Centre-ville",
      description: "Pharmacie proposant conseils, médicaments et produits de parapharmacie."
    }
  ];
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden shadow-xl border border-white/20"
    >
      <div className="p-6">
        <h3 className="text-2xl font-bold mb-4">Commerces partenaires</h3>
        <p className="text-white/80 mb-6">Découvrez tous les commerces participants à proximité de chez vous.</p>
      </div>
      
      <div className="relative w-full h-[400px] bg-gradient-to-br from-[#0056b3]/20 to-[#40E0D0]/20 overflow-hidden">
        {/* Points cardinaux stylisés */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-white/60 font-semibold">Nord</div>
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white/60 font-semibold">Sud</div>
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 font-semibold">Ouest</div>
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/60 font-semibold">Est</div>
        
        {/* Rues stylisées sur la carte */}
        <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-white/30"></div>
        <div className="absolute top-0 bottom-0 left-1/2 w-[2px] bg-white/30 transform -translate-x-1/2"></div>
        <div className="absolute top-[30%] left-0 right-0 h-[1px] bg-white/20"></div>
        <div className="absolute top-[70%] left-0 right-0 h-[1px] bg-white/20"></div>
        <div className="absolute top-0 bottom-0 left-[30%] w-[1px] bg-white/20"></div>
        <div className="absolute top-0 bottom-0 left-[70%] w-[1px] bg-white/20"></div>
        
        {/* Position de l'utilisateur */}
        <motion.div 
          className="absolute top-[50%] left-[50%] w-6 h-6 rounded-full bg-[#007BFF] border-2 border-white shadow-lg z-20"
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.2, 1] }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="absolute inset-0 rounded-full bg-[#007BFF]/50"
            animate={{ scale: [1, 1.8, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
          />
        </motion.div>
        
        {/* Commerces sur la carte */}
        {shops.map((shop) => (
          <motion.div
            key={shop.id}
            className="absolute w-10 h-10 z-10"
            style={{ top: shop.position.top, left: shop.position.left }}
            whileHover={{ scale: 1.1 }}
            onClick={() => setSelectedShop(selectedShop === shop.id ? null : shop.id)}
          >
            <motion.div 
              className={`w-full h-full rounded-lg shadow-lg flex items-center justify-center cursor-pointer ${
                selectedShop === shop.id ? 'bg-[#7B1FA2]' : 'bg-white/80'
              }`}
              animate={selectedShop === shop.id ? { scale: [1, 1.1, 1] } : {}}
              transition={{ repeat: selectedShop === shop.id ? Infinity : 0, duration: 1.5 }}
            >
              {shop.type === "Restaurant" && (
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${selectedShop === shop.id ? 'text-white' : 'text-[#007BFF]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {shop.type === "Librairie" && (
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${selectedShop === shop.id ? 'text-white' : 'text-[#007BFF]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              )}
              {shop.type === "Alimentation" && (
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${selectedShop === shop.id ? 'text-white' : 'text-[#007BFF]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              )}
              {shop.type === "Vêtements" && (
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${selectedShop === shop.id ? 'text-white' : 'text-[#007BFF]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              )}
              {shop.type === "Santé" && (
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${selectedShop === shop.id ? 'text-white' : 'text-[#007BFF]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              )}
            </motion.div>
          </motion.div>
        ))}
        
        {/* Info bulle du commerce sélectionné */}
        {selectedShop && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-4 left-4 right-4 bg-white/90 rounded-lg shadow-lg p-4 z-30"
          >
            {shops.filter(shop => shop.id === selectedShop).map(shop => (
              <div key={shop.id} className="text-[#0056b3]">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-lg">{shop.name}</h4>
                    <p className="text-[#0056b3]/80 text-sm">{shop.address}</p>
                  </div>
                  <div className="bg-[#007BFF] text-white px-2 py-1 rounded text-xs">
                    +{shop.points} pts
                  </div>
                </div>
                <p className="mt-2 text-sm">{shop.description}</p>
                <div className="mt-3 flex justify-end">
                  <button className="bg-[#007BFF] text-white text-sm px-3 py-1 rounded-md hover:bg-[#0056b3] transition-colors">
                    Plus d'infos
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </div>
      
      <div className="bg-white/5 p-4 flex justify-between items-center">
        <div className="flex gap-2">
          <span className="text-sm text-white/80">
            <span className="inline-block w-3 h-3 bg-[#007BFF] rounded-full mr-1"></span> Vous êtes ici
          </span>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="text-sm px-3 py-1 bg-white/20 rounded-md hover:bg-white/30 transition-colors"
        >
          Voir tous les commerces
        </motion.button>
      </div>
    </motion.div>
  );
};

export default MapSection;
